import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { validateInput } from "@/lib/inputValidation";
import { useTranslation } from "react-i18next";

interface UseLoginProps {
    onLogin: (token: string, playerId?: string) => void;
    setRestrictionMessage: (msg: string) => void;
    setShowRestrictionsDialog: (show: boolean) => void;
}

function redirectAfterAuth() {
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) window.location.href = decodeURIComponent(next);
    else if (window.location.pathname === '/login') window.location.href = '/';
}

export function useLogin({ onLogin, setRestrictionMessage, setShowRestrictionsDialog }: UseLoginProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    // Onglet initial : /signup ouvre l'inscription, sinon connexion
    const [activeTab, setActiveTab] = useState(
        typeof window !== "undefined" && window.location.pathname === "/signup" ? "register" : "login"
    );

    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const [showSuspensionDialog, setShowSuspensionDialog] = useState(false);
    const [suspensionData, setSuspensionData] = useState<{
        reason: string | null;
        suspendedUntil: string | null;
    } | null>(null);

    // Login classique (mot de passe) — utilisé seulement quand le compte a un mot de passe
    const loginMutation = useMutation({
        mutationFn: async (data: { email: string; password: string }) => {
            const response = await apiRequest("POST", "/api/auth/login", data);
            return response.json();
        },
        onSuccess: (data) => {
            if (data.token) {
                onLogin(data.token, data.playerId);
                toast({ description: t('auth.toasts.loginSuccess') });
                redirectAfterAuth();
            }
        },
        onError: (error: any) => {
            if (error.status === 403 && error.message === "Account suspended") {
                setSuspensionData({
                    reason: error.reason || null,
                    suspendedUntil: error.suspendedUntil || null,
                });
                setShowSuspensionDialog(true);
                return;
            }
            if (error.message?.includes("Failed to fetch") || error.name === 'TypeError') {
                return; // Erreur réseau — le handler global gère
            }
            toast({
                description: error.message || t('auth.toasts.loginFailed'),
                variant: "destructive",
            });
        },
    });

    // Étape 1 email-first : renvoie { hasPassword }
    const checkEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            const response = await apiRequest("POST", "/api/auth/check-email", { email });
            return response.json() as Promise<{ hasPassword: boolean }>;
        },
    });

    // Envoi (ou renvoi) d'un lien de connexion magique
    const requestMagicMutation = useMutation({
        mutationFn: async (email: string) => {
            const response = await apiRequest("POST", "/api/auth/request-magic", { email });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: t('auth.toasts.magicLinkSent') });
        },
        onError: (error: any) => {
            toast({
                description: error.message || t('auth.toasts.magicLinkFailed'),
                variant: "destructive",
            });
        },
    });

    // Signup passwordless : nom + prénom + email → connecté direct, email de confirmation en fond
    const signupMutation = useMutation({
        mutationFn: async (data: any) => {
            const response = await apiRequest("POST", "/api/auth/signup", data);
            return response.json();
        },
        onSuccess: (data) => {
            if (data.token) {
                onLogin(data.token, data.playerId);
                toast({ description: t('auth.toasts.signupSuccess') });
                redirectAfterAuth();
            }
        },
        onError: (error: any) => {
            let title = t('auth.toasts.registrationFailed');
            let description = error.message || t('auth.toasts.registrationFailed');
            if (error.type === "duplicate_email") {
                title = t('auth.errors.duplicateEmail');
                description = t('auth.errors.duplicateEmailDesc');
            }
            toast({ title, description, variant: "destructive", duration: 8000 });
        },
    });

    const guestLoginMutation = useMutation({
        mutationFn: async () => {
            const response = await apiRequest("POST", "/api/auth/guest");
            return response.json();
        },
        onSuccess: (data) => {
            if (data.token) {
                onLogin(data.token, data.playerId);
                toast({ description: t('auth.toasts.guestModeActivated') });
            }
        },
        onError: (error: any) => {
            toast({
                description: error.message || t('auth.toasts.guestModeFailed'),
                variant: "destructive",
            });
        },
    });

    const handleSignupSubmit = (data: any) => {
        const firstNameValidation = validateInput(data.firstName, "first name");
        if (!firstNameValidation.isValid) {
            setRestrictionMessage(firstNameValidation.error || t('auth.errors.invalidName'));
            setShowRestrictionsDialog(true);
            return;
        }
        const lastNameValidation = validateInput(data.lastName, "last name");
        if (!lastNameValidation.isValid) {
            setRestrictionMessage(lastNameValidation.error || t('auth.errors.invalidName'));
            setShowRestrictionsDialog(true);
            return;
        }

        const language = navigator.language ? navigator.language.split('-')[0] : 'en';
        const urlParams = new URLSearchParams(window.location.search);
        signupMutation.mutate({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            language,
            turnstileToken: data.turnstileToken,
            utmSource: urlParams.get("utm_source") || undefined,
            utmMedium: urlParams.get("utm_medium") || undefined,
            utmCampaign: urlParams.get("utm_campaign") || undefined,
        });
    };

    return {
        activeTab,
        setActiveTab,
        showTermsDialog,
        setShowTermsDialog,
        showSuspensionDialog,
        setShowSuspensionDialog,
        suspensionData,
        loginMutation,
        checkEmailMutation,
        requestMagicMutation,
        signupMutation,
        guestLoginMutation,
        handleSignupSubmit,
    };
}
